import { ENROLLMENT_STATUSES } from '@constants/individualPortal/dashboard/dashboardModelTypes';
import { isEmpty } from '@utils/miscellaneous/objectHelpers';
import { REGEX_WITH_ACCENTEDCHARS } from '@utils/validators/regex';
import { z } from 'zod';
import { isMonthOutsideOriginalEffectiveRange } from './enrollmentUtils';

// Define the Zod schema for enrollment
export const enrollmentSchema = (enrollment, userPermissions) =>
    z
        .object({
            enrollmentBenefitEffectiveStartDateUI: z.string().min(10, {
                message: 'error.member.enrollment_list.premiumhistory.enrollee.pleaseEnter.benefitStartDate',
            }),
            enrollmentBenefitEffectiveEndDateUI: z.string().min(10, {
                message: 'error.member.enrollment_list.premiumhistory.enrollee.enrollSubscriberEndDate',
            }),

            enrolleeDataDtoList: z.array(
                z.object({
                    firstName: z
                        .string()
                        .min(1, {
                            message: 'error.member.enrollment_list.premiumhistory.enrollee.pleaseEnter.firstName',
                        })
                        .regex(REGEX_WITH_ACCENTEDCHARS, {
                            message: 'error.member.enrollment_list.premiumhistory.enrollee.valid.firstName',
                        }),
                    middleName: z
                        .string()
                        .regex(REGEX_WITH_ACCENTEDCHARS, {
                            message: 'error.member.enrollment_list.premiumhistory.enrollee.valid.middleName',
                        })
                        .nullable()
                        .optional(),
                    lastName: z
                        .string()
                        .min(1, {
                            message: 'error.member.enrollment_list.premiumhistory.enrollee.pleaseEnter.lastName',
                        })
                        .regex(REGEX_WITH_ACCENTEDCHARS, {
                            message: 'error.member.enrollment_list.premiumhistory.enrollee.valid.lastName',
                        }),
                    genderLookupLabel: z.string(),
                    taxIdentificationNumber: z.string().nullable(),
                    birthDate: z.string().optional(),
                    enrolleeEffectiveStartDateUI: z.string().min(10, {
                        message: 'error.member.enrollment_list.premiumhistory.enrollee.pleaseEnter',
                    }),
                    enrolleeEffectiveEndDateUI: z.string().min(10, {
                        message: 'error.member.enrollment_list.premiumhistory.enrollee.pleaseEnterEndDate',
                    }),
                }),
            ),
            enrollmentPremiumDtoList: z
                .array(
                    z.object({
                        month: z.number(),
                        year: z.number(),
                        slcspPremiumAmount: z.number().optional(),
                        isFinancial: z.boolean().optional(),
                        isAptcEligible: z.boolean().optional(),
                        isStateSubsidyEligible: z.boolean().optional(),
                        aptcAmount: z.union([z.number(), z.string()]).nullable().optional(),
                        grossPremiumAmount: z.number().optional(),
                        maxAptc: z.union([z.number(), z.string()]).nullable().optional(),
                        maxStateSubsidy: z.union([z.number(), z.string()]).nullable().optional(),
                        minimumPremium: z.number().optional(),
                        netPremiumAmount: z.union([z.number(), z.string()]).nullable().optional(),
                        stateSubsidyAmount: z.union([z.number(), z.string()]).nullable().optional(),
                        stateSubsidy2Amount: z.union([z.number(), z.string()]).nullable().optional(),
                        maxStateSubsidy2: z.number().nullable().optional(),
                        medicaidTransitionAmount: z.number().nullable().optional(),
                        isMedicaidTransitionEligible: z.boolean().optional(),
                    }),
                )
                .superRefine((data, ctx) => validatedSubsidyAmounts(data, ctx, enrollment, userPermissions)),
        })
        .superRefine((data, ctx) => DateValidations(data, ctx, enrollment));

const validatedSubsidyAmounts = (enrollmentPremiumDtoList, ctx, enrollment, userPermissions) => {
    enrollmentPremiumDtoList.forEach(data => {
        const ehbPercent = enrollment.ehbPercentage;

        if (data.isFinancial && !isMonthOutsideOriginalEffectiveRange(data.month, enrollment)) {
            if (data.isAptcEligible) {
                validateAptc(enrollment, data, ehbPercent, ctx);
                validateMaxAptc(enrollment, data, ehbPercent, ctx);
            }
            if (
                enrollment.isStateSubsidyEnabled &&
                userPermissions?.memberEnrlmntAptcEditable &&
                enrollment?.enrollmentStatus !== ENROLLMENT_STATUSES.PEND &&
                enrollment?.enrollmentStatus !== ENROLLMENT_STATUSES.PEND_CANCELLED
            ) {
                validateStateSubsidy(enrollment, data, ehbPercent, ctx);
                validateMaxStateSubsidy(enrollment, data, ehbPercent, ctx);
            }
            if (enrollment.isStateSubsidy2Enabled) {
                validateStateSubsidy2(enrollment, data, ehbPercent, ctx);
            }
        }
    });
    return z.NEVER;
};

const validateAptc = (enrollment, premium, ehbPercent, ctx) => {
    const rowIndex = premium.month - 1;
    if (isEmpty(premium.aptcAmount)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [rowIndex, 'aptcAmount'],
            message: 'error.member.enrollment_list.premiumhistory.validNumber',
        });
        return;
    }
    if (Number((premium.grossPremiumAmount * (ehbPercent || 1)).toFixed(2)) < Number(premium.aptcAmount)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [rowIndex, 'aptcAmount'],
            message: 'error.member.enrollment_list.premiumhistory.aptc.lessOrEqual2',
        });
    } else if (premium.maxAptc !== undefined && Number(premium.aptcAmount) > Number(premium.maxAptc)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [rowIndex, 'aptcAmount'],
            message: 'error.member.enrollment_list.premiumhistory.aptc.lessOrEqual',
        });
    }
};

const validateMaxAptc = (enrollment, premium, ehbPercent, ctx) => {
    const rowIndex = premium.month - 1;
    if (isEmpty(premium.maxAptc)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [rowIndex, 'maxAptc'],
            message: 'error.member.enrollment_list.premiumhistory.validNumber',
        });
        return;
    }
    if (Number(premium.aptcAmount) > (Number(premium.maxAptc) || 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [rowIndex, 'maxAptc'],
            message: 'error.member.enrollment_list.premiumhistory.caps.greaterOrEqual',
        });
    }
};

const validateStateSubsidy = (enrollment, premium, ehbPercent, ctx) => {
    const rowIndex = premium.month - 1;
    if (Number(premium.stateSubsidyAmount) > Number(premium.maxStateSubsidy)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [rowIndex, 'stateSubsidyAmount'],
            message: 'error.member.enrollment_list.premiumhistory.stateSubsidy.lessOrEqual',
        });
    } else if (
        premium.stateSubsidyAmount > 0 &&
        Number(premium.aptcAmount) <= Number((premium.grossPremiumAmount * (ehbPercent || 1)).toFixed(2)) &&
        Number(premium.grossPremiumAmount.toFixed(2)) <
            Number(
                (
                    (Number(premium.aptcAmount) || 0) +
                    (Number(premium.stateSubsidyAmount) || 0) +
                    (Number(premium.stateSubsidy2Amount) || 0)
                ).toFixed(2),
            )
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [rowIndex, 'stateSubsidyAmount'],
            message: 'error.member.enrollment_list.premiumhistory.caps.lessOrEqual',
        });
    }
};

const validateMaxStateSubsidy = (enrollment, premium, ehbPercent, ctx) => {
    const rowIndex = premium.month - 1;
    if (Number(premium.stateSubsidyAmount) > (Number(premium.maxStateSubsidy) || 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [rowIndex, 'maxStateSubsidy'],
            message: 'error.member.enrollment_list.premiumhistory.caps.greaterOrEqual',
        });
    }
};

const validateStateSubsidy2 = (enrollment, premium, ehbPercent, ctx) => {
    const rowIndex = premium.month - 1;
    if (
        Number(premium.stateSubsidy2Amount) > 0 &&
        Number(premium.aptcAmount) <= Number((premium.grossPremiumAmount * (ehbPercent || 1)).toFixed(2)) &&
        Number(premium.grossPremiumAmount.toFixed(2)) <
            Number(
                (
                    (Number(premium.aptcAmount) || 0) +
                    (Number(premium.stateSubsidyAmount) || 0) +
                    (Number(premium.stateSubsidy2Amount) || 0)
                ).toFixed(2),
            )
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [rowIndex, 'stateSubsidy2Amount'],
            message: 'error.member.enrollment_list.premiumhistory.caps.lessOrEqual',
        });
    }
};

const DateValidations = (data, ctx, enrollment) => {
    const birthDate = new Date(data.enrolleeDataDtoList?.[0]?.birthDate || '');
    const enrollmentBenefitEffectiveStartDateUI = new Date(data.enrollmentBenefitEffectiveStartDateUI);
    const enrollmentBenefitEffectiveEndDateUI = new Date(data.enrollmentBenefitEffectiveEndDateUI);
    const coverageYear = enrollment.enrollmentPremiumDtoList?.[0].year;
    const startYear = enrollmentBenefitEffectiveStartDateUI.getFullYear();
    const endYear = new Date(data.enrollmentBenefitEffectiveEndDateUI).getFullYear();

    if (birthDate > enrollmentBenefitEffectiveStartDateUI) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['enrollmentBenefitEffectiveStartDateUI'],
            message: 'error.member.enrollment_list.premiumhistory.enrollee.early.benefitStartDate',
        });
    }

    if (startYear != coverageYear || endYear != coverageYear) {
        if (startYear != coverageYear) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['enrollmentBenefitEffectiveStartDateUI'],
                message: 'error.member.enrollment_list.premiumhistory.startDateWithin',
            });
        }
        if (endYear != coverageYear) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['enrollmentBenefitEffectiveEndDateUI'],
                message: 'error.member.enrollment_list.premiumhistory.endDateWithin',
            });
        }
    } else {
        data.enrolleeDataDtoList.map((enrollee, index) => {
            if (index !== 0) {
                const enroleeStartDate = new Date(enrollee.enrolleeEffectiveStartDateUI);
                const enrolleeEndDate = new Date(enrollee.enrolleeEffectiveEndDateUI);

                if (enroleeStartDate < enrollmentBenefitEffectiveStartDateUI) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['enrolleeDataDtoList', index, 'enrolleeEffectiveStartDateUI'],
                        message: 'error.member.enrollment_list.premiumhistory.datePrecede',
                    });
                } else if (enroleeStartDate > enrollmentBenefitEffectiveEndDateUI) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['enrolleeDataDtoList', index, 'enrolleeEffectiveStartDateUI'],
                        message: 'error.member.enrollment_list.premiumhistory.startDateWithin',
                    });
                }
                if (
                    enrollmentBenefitEffectiveEndDateUI < enrolleeEndDate ||
                    enrollmentBenefitEffectiveStartDateUI > enrolleeEndDate
                )
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['enrolleeDataDtoList', index, 'enrolleeEffectiveEndDateUI'],
                        message: 'error.member.enrollment_list.premiumhistory.endDateWithin',
                    });
            }
        });
    }

    if (data.enrollmentBenefitEffectiveEndDateUI === data.enrollmentBenefitEffectiveStartDateUI) {
        if (enrollment.enrollmentBenefitEffectiveStartDateUI !== data.enrollmentBenefitEffectiveStartDateUI) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['enrollmentBenefitEffectiveStartDateUI'],
                message: 'error.member.enrollment_list.premiumhistory.enrollee.cancellation.benefitStartDate',
            });
        }
        if (enrollment.enrollmentBenefitEffectiveEndDateUI !== data.enrollmentBenefitEffectiveEndDateUI) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['enrollmentBenefitEffectiveEndDateUI'],
                message: 'error.member.enrollment_list.premiumhistory.enrollee.enrollSubscriber',
            });
        }
    }
};
